import { useEffect, useState, type CSSProperties } from 'react';
import Card from '../components/card/Card';
import Icon from '../components/icon/Icon';
import Label from '../components/texts/label/Label';
import Button from '../components/button/Button';
import { useNavigate } from 'react-router-dom';
import { getPrimaryRoleDisplayLabel, getRolesFromToken } from '../utils/Utils';
import { routes } from '../routes/routePaths';
import { useSgrData } from '../hooks/useSgrData';
import { useClientLogo } from '../hooks/useClientLogo';
import { resolveRouteForRoles } from '../utils/resolveRouteForRoles';
import { useClientTheme } from '../context/ThemeContext';
import { resolveNavbarBranding } from '../utils/navbarBranding';
import { getRuntimeEnv } from '../utils/runtimeEnv';

/** Figma Atlantic header: real CSS border (DS secondary uses inset shadow / grey text). */
const figmaUyLogoutBtn: CSSProperties = {
  background: '#FFFFFF',
  color: '#E72419',
  border: '1px solid #E72419',
  borderRadius: 6,
  padding: '6px 24px',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  fontFamily: 'Inter, sans-serif',
  flexShrink: 0,
};

const NavBar: React.FC = () => {
  const name = localStorage.getItem('full_name');
  const role = localStorage.getItem('roles');
  const [currentRole, setCurrentRole] = useState('');
  const userLetter = name?.substring(0, 2).toUpperCase();
  const navigate = useNavigate();
  const { sgrName, sgrLogo } = useSgrData();
  const clientName = getRuntimeEnv('REACT_APP_CLIENT_NAME');
  const { logoUrl: clientLogoUrl } = useClientLogo();
  const { clientTheme } = useClientTheme();
  const preferClientBranding = clientName === 'aca';
  const { logoUrl: displayLogo, displayName } = resolveNavbarBranding({
    sgrLogo,
    sgrName,
    clientLogoUrl,
    clientThemeName: clientTheme?.name ?? null,
    preferClientBranding,
  });

  useEffect(() => {
    if (role) {
      setCurrentRole(getPrimaryRoleDisplayLabel(getRolesFromToken()));
    }
  }, [role]);

  const logout = () => {
    localStorage.clear();
    navigate(routes.login);
  };

  return (
    <Card
      width='-webkit-fill-available'
      maxWidth='100vw'
      variant='neutral'
      spacing={{ top: clientTheme ? '1' : '2', right: '3', bottom: clientTheme ? '1' : '2', left: '3' }}
      borderRadius={false}
      modifier='inverted'
      display='flex'
      justifyContent='space-between'
      style={clientTheme ? { height: 50, boxSizing: 'border-box' } : undefined}
    >
      <Card
        display='flex'
        alignItems='center'
        gap='16px'
        onClick={() =>
          navigate(resolveRouteForRoles(getRolesFromToken(), routes.dashboard))
        }
        style={{ cursor: 'pointer' }}
      >
        {displayLogo ? (
          clientName === 'aca' && displayName ? (
            <>
              <img
                src={displayLogo}
                alt=''
                aria-hidden
                style={{ height: '39px', width: 'auto' }}
              />
              <Label type='1' weight='bold' style={{ fontSize: '18px' }}>
                {displayName}
              </Label>
            </>
          ) : (
            <img
              src={displayLogo}
              alt={displayName || 'Logo SGR'}
              style={{ height: '39px', width: 'auto' }}
            />
          )
        ) : (
          <>
            <Icon height={39} name='isotipo' width={39} />
            {displayName ? (
              <Label type='1' weight='bold' style={{ fontSize: '18px' }}>
                {displayName}
              </Label>
            ) : (
              <Icon name='logo' variant='secondary-1' modifier='strong' width={80} />
            )}
          </>
        )}
      </Card>
      <Card display='flex' gap='16px' alignItems='center' style={{ minWidth: 0, flexShrink: 1 }}>
        <Card width='1px' variant='success'></Card>
        {clientTheme && (
          <Icon
            name='notification_outline'
            variant='neutral'
            modifier='regular'
            width={24}
            height={24}
            cursor='default'
          />
        )}
        <Card
          border={true}
          variant={clientTheme ? 'neutral' : 'info'}
          spacing={'2'}
          modifier={clientTheme ? 'weak' : 'inverted'}
          display='flex'
          alignItems='center'
          justifyContent='center'
          borderRadiusAmount='5'
          style={
            clientTheme
              ? {
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  backgroundColor: '#E8E8E8',
                  border: 'none',
                }
              : undefined
          }
        >
          <Label weight='bold' type='1'>
            {userLetter}
          </Label>
        </Card>
        <Card
          display='grid'
          spacing={{ top: '0', right: '3' }}
          alignItems='center'
          style={{ minWidth: 0, maxWidth: '180px', overflow: 'hidden' }}
        >
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Label type='4'>{currentRole}</Label>
          </div>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Label type='2' weight='bold'>
              {name}
            </Label>
          </div>
        </Card>
        {clientTheme ? (
          <button type='button' onClick={logout} style={figmaUyLogoutBtn}>
            Cerrar sesión
          </button>
        ) : (
          <Button variant='secondary' size='md' onClick={logout}>
            Cerrar sesión
          </Button>
        )}
      </Card>
    </Card>
  );
};

export default NavBar;
